/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { useState, Fragment } from 'react'

import { Loader, CssTooltip } from '@livechat/design-system'
import noAnswersIcon from '../assets/icons/no-asnwers.svg'
import MarkdownRenderer from './MarkdownRenderer'

const chartCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: auto;
  min-height: 400px;
  background-color: white;

  width: calc(100% - 40px);
  max-width: 500px;
  padding: 20px;
  padding-top: 25px;
  border-radius: 10px;
  box-shadow: rgb(228 228 228) 0px 1px 5px 0px;

  :nth-of-type(2n) {
    margin-right: auto;
  }

  :nth-of-type(2n + 1) {
    margin-left: auto;
  }

  @media (max-width: 980px) {
    margin: 0 !important;
  }

  .question {
    font-weight: 400;
    text-align: center;
    margin-bottom: 30px;
    font-size: 18px;
  }

  .pie {
    width: 170px;
  }

  .answers {
    margin-top: 20px;
  }

  .loader {
  }
`

const labelCss = css`
  display: flex;
  align-items: center;
  color: hsl(0, 0%, 70%);
  font-weight: 100;

  > img {
    margin-right: 7px;
  }
`

const scoreCss = css`
  display: flex;
  justify-content: space-between;
  min-width: 170px;
  height: 150px;
  margin-top: 40px;

  .lc-radio__label {
    display: flex;
    flex-direction: column-reverse;
  }

  .lc-radio__text {
    width: 64%;
    text-align: center;
  }
`

const columnCss = (height, isCurrNone) => css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: column;
  padding: 5px;
  font-weight: 300;
  height: 100%;

  .bar {
    width: 20px;
    height: ${height}px;
    min-height: 2px;
    background-color: #85a3f3;
    margin-bottom: 5px;
    position: relative;
    cursor: pointer;
  }

  .score {
    ${isCurrNone && 'color: hsl(0,0%,70%);'}
  }
`

const labelsCss = (width) => css`
  width: ${width}px;
  min-width: 170px;
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-weight: 300;

  > span {
    max-width: 40%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .right {
    text-align: right;
  }
`

const ScoreChart = ({ field, fieldIndex, response, isLoading }) => {
  const [openedTooltop, setOpenedTooltip] = useState()

  const renderChart = () => {
    const { range, labels } = field.score

    const numberOfColumns = parseInt(range.max) - parseInt(range.min) + 1

    return (
      <Fragment>
        <div key={`field-${fieldIndex}-${field.question}`} css={scoreCss}>
          {Array.from(
            {
              length: numberOfColumns,
            },
            (_, i) => i + parseInt(range.min)
          ).map((score) => {
            const scoreSum = response.map(
              (e) => e.response?.[fieldIndex]
            )?.length

            const currScore = response
              .map((e) => e.response?.[fieldIndex])
              .filter((e) => e === score)?.length

            const isCurrNone = !currScore

            return (
              <div
                key={`score-${field.question}-${score}`}
                css={columnCss(147 * (currScore / scoreSum), isCurrNone)}
              >
                <div
                  className="bar"
                  onMouseEnter={() =>
                    setOpenedTooltip(`${fieldIndex}-${score}`)
                  }
                  onMouseLeave={() => setOpenedTooltip()}
                >
                  <CssTooltip
                    placement="top"
                    isVisible={openedTooltop === `${fieldIndex}-${score}`}
                    width="150px"
                    zIndex={100}
                  >
                    <div
                      css={css`
                        max-width: 150px;
                        text-align: center;
                      `}
                    >
                      Ansers: {currScore} (
                      {parseInt((currScore / scoreSum) * 100, 10)}%)
                    </div>
                  </CssTooltip>
                </div>
                <span className="score">{score}</span>
              </div>
            )
          })}
        </div>
        {labels.min && labels.max && (
          <div css={labelsCss(30 * numberOfColumns)}>
            <span className="left">{labels.min}</span>
            <span className="right">{labels.max}</span>
          </div>
        )}
      </Fragment>
    )
  }

  const question = `${field.question} (${field.score?.range?.min}-${field.score?.range?.max})`

  return (
    <div css={chartCss}>
      <div className="question">
        <MarkdownRenderer>{question}</MarkdownRenderer>
      </div>

      {isLoading ? (
        <Loader className="loader" size="large" />
      ) : response?.length > 0 ? (
        renderChart()
      ) : (
        <span css={labelCss}>
          <img src={noAnswersIcon} alt="no answers" />{' '}
          <span>No answers given</span>
        </span>
      )}
    </div>
  )
}

export default ScoreChart
